'use client';

import Link from 'next/link';
import { useLocalizedPageLinks } from '@/contexts/LocalizedPageLinks';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import type { LocaleSwitcherProps } from '@routeroyal/theme-core';

/**
 * Stone Investment Locale Switcher
 * 
 * Concept One Design (Exact Match):
 * - Hover-expandable pill design
 * - Collapsed: shows current language (EN/FR)
 * - Expanded: shows EN / FR toggle
 * - Uses font-sans (Montserrat) for text
 * - Custom easing: ease-[0.22,1,0.36,1]
 */
export function StoneInvestmentLocaleSwitcher({ 
  locale = 'en',
  variant = 'default',
  className = '',
}: LocaleSwitcherProps) {
  const [isHovered, setIsHovered] = useState(false);
  const pathname = usePathname();
  const localisedLinks = useLocalizedPageLinks();

  const activeLang = locale.toUpperCase();

  const getLocalePath = (targetLocale: string) => {
    // Missing translations lead to the journal hub rather than a fake translated article.
    if (localisedLinks) return localisedLinks[targetLocale] || `/${targetLocale}/news`;
    if (!pathname) return `/${targetLocale}`;
    const segments = pathname.split('/');
    const secondSegment = segments[1];
    if (segments.length >= 2 && secondSegment && ['en', 'fr', 'es'].includes(secondSegment)) {
      segments[1] = targetLocale;
      return segments.join('/');
    }
    return `/${targetLocale}`;
  };

  // Minimal variant - simple inline
  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Link
          href={getLocalePath('en')}
          className={`text-xs font-sans uppercase tracking-widest transition-colors duration-300 ${
            locale === 'en' ? 'font-bold' : 'font-light opacity-70 hover:opacity-100'
          }`}
        >
          EN
        </Link>
        <span className="text-xs opacity-50 font-light" aria-hidden="true">/</span>
        <Link
          href={getLocalePath('fr')}
          className={`text-xs font-sans uppercase tracking-widest transition-colors duration-300 ${
            locale === 'fr' ? 'font-bold' : 'font-light opacity-70 hover:opacity-100'
          }`}
        >
          FR
        </Link>
      </div>
    );
  }

  const handleToggle = () => setIsHovered(prev => !prev);

  // Default: Hover/tap-expandable pill design (Concept One style)
  return (
    <div 
      className={`relative z-50 pointer-events-auto ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggle(); }}
    >
      <div 
        className={`
          relative h-8 flex items-center overflow-hidden
          rounded-full transition-all duration-500 ease-concept
          ${isHovered 
            ? 'w-24 border border-current' 
            : 'w-8 justify-center border border-transparent'
          }
        `}
      >
        {/* COLLAPSED STATE: Active Language Indicator */}
        <div 
          className={`
            absolute inset-0 flex items-center justify-center transition-all duration-300
            ${isHovered ? 'opacity-0 scale-75 blur-sm' : 'opacity-100 scale-100 blur-0'}
          `}
        >
          <span className="text-xs font-sans uppercase tracking-widest font-medium">
            {activeLang}
          </span>
        </div>

        {/* EXPANDED STATE: Selection Controls */}
        <div 
          className={`
            absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500 ease-concept
            ${isHovered ? 'opacity-100 translate-y-0 delay-75' : 'opacity-0 translate-y-4 pointer-events-none'}
          `}
        >
          <Link
            href={getLocalePath('en')}
            onClick={(e) => e.stopPropagation()}
            className={`
              text-xs font-sans uppercase tracking-widest transition-colors duration-300
              ${locale === 'en' ? 'font-bold' : 'font-light opacity-70 hover:opacity-100'}
            `}
          >
            EN
          </Link>
          
          <span className="text-xs opacity-50 font-light" aria-hidden="true">/</span>
          
          <Link
            href={getLocalePath('fr')}
            onClick={(e) => e.stopPropagation()}
            className={`
              text-xs font-sans uppercase tracking-widest transition-colors duration-300
              ${locale === 'fr' ? 'font-bold' : 'font-light opacity-70 hover:opacity-100'}
            `}
          >
            FR
          </Link>
        </div>
      </div>
    </div>
  );
}
